import { useEffect, useRef } from "react";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import styled, { css } from "styled-components";

export type CheckedState = "checked" | "unchecked" | "indeterminate";

/** Props for the checkbox component. */
export interface CheckboxProps {
  label?: string;
  checked: CheckedState;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
}

/** Checkbox with optional label and disabled state. */
export const Checkbox = ({
  label,
  checked,
  disabled,
  onChange,
}: CheckboxProps): JSX.Element => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isChecked = checked === "checked";
  const showMixed = checked === "indeterminate";

  useEffect(() => {
    if (!inputRef.current) return;
    inputRef.current.indeterminate = showMixed;
  }, [showMixed]);

  return (
    <Label $disabled={Boolean(disabled)}>
      <HiddenInput
        ref={inputRef}
        type="checkbox"
        checked={isChecked}
        aria-checked={showMixed ? "mixed" : isChecked}
        disabled={disabled}
        onChange={() => onChange?.(!isChecked)}
      />
      <Box $checked={isChecked || showMixed} $disabled={Boolean(disabled)}>
        {isChecked && !showMixed && (
          <CustomIcon name="v2/checkmark" size="20px" color={ColorV2.White} />
        )}
        {showMixed && <IndeterminateMark />}
      </Box>
      {label && <Text $disabled={Boolean(disabled)}>{label}</Text>}
    </Label>
  );
};

const Label = styled.label<{ $disabled: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
`;

const HiddenInput = styled.input`
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 0;
  height: 0;
`;

const Box = styled.span<{ $checked: boolean; $disabled: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid ${ColorV2.Blue1};
  background-color: ${ColorV2.White};

  ${(props) =>
    props.$checked &&
    css`
      background-color: ${ColorV2.Blue1};
      border-color: ${ColorV2.Blue1};
    `}

  ${(props) =>
    props.$disabled &&
    css`
      border-color: ${ColorV2.Blue5};
      background-color: ${props.$checked ? ColorV2.Blue5 : ColorV2.White};
    `}
`;

const Text = styled.span<{ $disabled: boolean }>`
  ${TypographyV2.b1}
  color: ${(props) => (props.$disabled ? ColorV2.Blue5 : ColorV2.Blue1)};
`;

const IndeterminateMark = styled.span`
  width: 12px;
  height: 2px;
  border-radius: 999px;
  background: ${ColorV2.White};
`;
